import type { LocalRow, SeedTables } from '../types/localDb.types'
import { seedAudit } from './platform.seed'
import { SEED_GROUP, seedId } from './seedId'

/**
 * Master Data (MST-01/03/03A/06/07/08/09/10/11) — pelengkap seed ERD v010 untuk tabel yang SUDAH dipakai cluster lain:
 * `mt_product`, `mt_bill_of_material`, `mt_unit`, `mt_reject_type`, `mt_rack(_bin)`, `warehouse_zone`, `pallet`.
 * Yang ditambahkan di sini hanya yang belum ada: `sla_wms_setting` (Master SLA), gudang satelit WH-SGI beserta
 * rak-nya, posisi pallet kedua/ketiga per level rak RM, dan atribut tampilan SKU (Kode SKU, Jenis Rokok, GTIN,
 * dimensi). Kolom di luar ERD diusulkan di `ERD-EXTENSION-PROPOSAL.md` §13 (D36–D44).
 */

/* ------------------------------------------------------------ SKU FG: kode, jenis, GTIN, dimensi */

type Dims = Record<string, string>
/** [kode produk repo, Kode SKU label/batch, jenis rokok, GTIN Pack, dimensi per level]. */
const SKU_META: [string, string, 'FILTER' | 'KRETEK', string, Dims][] = [
  [
    'TBC-ONE-12',
    'TO12',
    'FILTER',
    '8997034030610',
    { Pack: '9,5 x 5,5 x 2', Slop: '9,6 x 5,6 x 20,5', Bal: '29 x 20 x 21', Karton: '60 x 30 x 45' }
  ],
  ['TBC-ONE-F16', 'TO16', 'FILTER', '8997034030627', {}],
  [
    'TBC-BOLD-20',
    'TB20',
    'FILTER',
    '8997034030719',
    { Pack: '9,5 x 5,5 x 2,2', Slop: '9,6 x 5,6 x 22', Bal: '29 x 20 x 23', Karton: '60 x 30 x 47' }
  ],
  ['TBC-BOLD-20-SKT', 'TB20T', 'FILTER', '8997034030719', {}],
  ['TBC-BOLD-12', 'TB12', 'FILTER', '8997034030702', {}],
  ['TBC-XTRA-20', 'TX20', 'FILTER', '8997034030405', {}],
  ['BRIGHTON-RED', 'BR20', 'FILTER', '8997034030795', {}],
  ['ARUM-MANIS', 'AM12', 'KRETEK', '8997034030344', {}],
  ['AMANA-K12', 'AK12', 'KRETEK', '8997034030887', {}],
  ['MILLIONS', 'MI20', 'FILTER', '8997034030740', {}]
]

/* ------------------------------------------------------------ unit: keterangan */

const UNIT_NOTE: Record<string, string> = {
  BOX: 'Kardus besar berlabel — etiket, OPP press',
  ROLL: 'Gulungan — papir, foil, inner, CTP, teartape',
  DOS: 'Dos filter',
  SAK: 'Karung tembakau rajangan',
  BUNDLE: 'Ikatan lembaran kraft',
  PAIL: 'Ember lem',
  RIM: 'Papir lembaran — satuan CV Karya Harapan',
  PAK: 'Papir lembaran — satuan PT Indra Jaya',
  PCS: 'Keping — etiket, OPP press',
  M: 'Meter — barang gulungan',
  BTG: 'Batang — filter',
  KG: 'Kilogram — tembakau, lem',
  LEMBAR: 'Lembar — kraft, papir lembaran'
}

/* ------------------------------------------------------------ SLA (prototype `master.sla`) */

export type SlaUnit = 'HOUR' | 'MINUTE' | 'DAY' | 'PERCENT'
/** [kode, grup, nama, keterangan, nilai, satuan]. Urutan = urutan tampil. */
export const SLA_DEFS: [string, string, string, string, number, SlaUnit][] = [
  [
    'FG_APPROVE_REMIND',
    'INBOUND_FG',
    'Pengingat approval Supervisor',
    'Batch yang belum diputuskan Supervisor diberi tanda kuning dan Supervisor diingatkan.',
    1,
    'HOUR'
  ],
  [
    'FG_APPROVE_ESCALATE',
    'INBOUND_FG',
    'Disposisi naik ke Kadiv',
    'Batch yang belum diputuskan naik ke Kadiv WH FG; Kadiv bisa memutuskan menggantikan Supervisor.',
    2,
    'HOUR'
  ],
  [
    'FG_APPROVE_AUTO',
    'INBOUND_FG',
    'Otomatis Setujui',
    'Tanpa keputusan siapa pun sampai ambang ini, batch otomatis DISETUJUI agar antreannya tidak macet. Keputusan otomatis ini final.',
    8,
    'HOUR'
  ],
  [
    'FG_RECEIVE_LIMIT',
    'INBOUND_FG',
    'Batas terima batch auto labeler',
    'Batch dianggap terlambat diterima bila melewati ambang ini sejak dikirim mesin.',
    4,
    'HOUR'
  ],
  [
    'OFG_RELEASE_ESCALATE',
    'OUTBOUND_FG',
    'Disposisi keputusan Release',
    'Release (termasuk Bongkar Kemasan) yang belum diputuskan Supervisor naik ke Kadiv WH FG. Jam dinding, tidak pernah otomatis.',
    1,
    'HOUR'
  ],
  [
    'GAB_ALM_AUTO_APPROVE',
    'MERGE_ALM',
    'Auto-approve permintaan ALM',
    'Permintaan penggabungan dari start ALM yang belum diputuskan Admin WH FG sampai batas ini DISETUJUI otomatis oleh sistem; Supervisor hanya diberi tahu. Admin tetap dapat menyetujui atau menolak kapan saja sebelum batas.',
    5,
    'MINUTE'
  ],
  [
    'CLM_AUTO_APPROVE',
    'CLAIM',
    'Auto-approve hasil sortir claim',
    'Hasil sortir yang belum diputuskan Supervisor sampai batas ini DISETUJUI otomatis. Pilihan cakupan menentukan apakah barang Not Good ikut disetujui otomatis (claim sudah disetujui Purchasing di awal).',
    15,
    'MINUTE'
  ],
  [
    'DSP_APPROVE_ESCALATE',
    'DESTRUCTION_FG',
    'Disposisi keputusan Not Good',
    'Keputusan atas barang Not Good yang masih tertahan naik ke Kadiv WH FG.',
    2,
    'HOUR'
  ],
  [
    'QUARANTINE_MAX_AGE',
    'INBOUND_RM',
    'Umur maksimum Karantina Kedatangan',
    'Batch yang menua melewati ambang ini ditandai merah di dashboard dan papan QC.',
    3,
    'DAY'
  ],
  [
    'ROP_BUFFER',
    'INBOUND_RM',
    'Buffer Reorder Point',
    'Hari cadangan di atas lead time supplier terlama saat menghitung usulan Reorder Point. Makin besar, makin cepat status PESAN muncul.',
    3,
    'DAY'
  ],
  [
    'OPNAME_TARGET_MINUTES',
    'STOCK_OPNAME',
    'Target durasi satu sesi opname',
    'Dipakai laporan opname untuk menilai sesi selesai tepat waktu atau lewat target.',
    240,
    'MINUTE'
  ],
  [
    'OPNAME_MATCHED_TARGET',
    'STOCK_OPNAME',
    'Target akurasi Matched',
    'Garis target pada grafik tren akurasi antar sesi opname.',
    98,
    'PERCENT'
  ],
  [
    'DSC_ESCALATE_RM',
    'STOCK_OPNAME',
    'Eskalasi selisih opname — Gudang RM',
    'Selisih yang masih berstatus Open lewat H+n (akhir hari kalender WIB) dieskalasi otomatis ke Kadiv WH RM.',
    5,
    'DAY'
  ],
  [
    'DSC_APPROVAL_RM',
    'STOCK_OPNAME',
    'Batas approval selisih — Gudang RM',
    'Tenggat Kadiv WH RM memutuskan write-off, dihitung H+n sejak selisih terbentuk (penanda tenggat; tidak ada keputusan otomatis).',
    7,
    'DAY'
  ],
  [
    'DSC_ESCALATE_FG',
    'STOCK_OPNAME',
    'Eskalasi selisih opname — Gudang FG',
    'Selisih yang masih berstatus Open lewat H+n (0 = akhir hari yang sama) dieskalasi otomatis ke Kadiv WH FG.',
    0,
    'DAY'
  ],
  [
    'DSC_APPROVAL_FG',
    'STOCK_OPNAME',
    'Batas approval selisih — Gudang FG',
    'Tenggat Kadiv WH FG memutuskan write-off, dihitung H+n sejak selisih terbentuk (penanda tenggat; tidak ada keputusan otomatis).',
    1,
    'DAY'
  ]
]

/**
 * Aturan per baris Master SLA (laporan 28 §9.1): `toggle` = aturan otomatis yang bisa dimatikan (`is_enabled`);
 * `units` = satuan yang boleh dipilih; `min`/`max` dalam MENIT untuk satuan waktu (MINUTE/HOUR/DAY), nilai mentah untuk PERCENT;
 * `allowZero` = H+0 sah; `scope` = opsi cakupan (disimpan di `scope_json`). Dibaca handler simpan dan penyimpan FE —
 * bukan angka mati di layar.
 */
export type SlaScopeDef = { key: string; label: string; help: string; default: boolean }
export type SlaMeta = {
  toggle: boolean
  units: SlaUnit[]
  min: number
  max: number
  allowZero?: boolean
  scope?: SlaScopeDef[]
}
const TIME_ANY: SlaUnit[] = ['MINUTE', 'HOUR']
export const SLA_META: Record<string, SlaMeta> = {
  GAB_ALM_AUTO_APPROVE: { toggle: true, units: TIME_ANY, min: 1, max: 24 * 60 },
  CLM_AUTO_APPROVE: {
    toggle: true,
    units: TIME_ANY,
    min: 1,
    max: 72 * 60,
    scope: [
      {
        key: 'include_not_good',
        label: 'Sertakan barang Not Good',
        help: 'Ya: Not Good ikut disetujui otomatis. Tidak: Not Good tetap menunggu keputusan Supervisor.',
        default: true
      }
    ]
  },
  FG_APPROVE_AUTO: { toggle: true, units: TIME_ANY, min: 60, max: 72 * 60 },
  /* Disposisi ke Kadiv (v7.50 C-13): dimatikan = tidak pernah naik ke Kadiv, keputusan tetap di Supervisor. */
  FG_APPROVE_ESCALATE: { toggle: true, units: ['HOUR'], min: 60, max: Number.MAX_SAFE_INTEGER },
  OFG_RELEASE_ESCALATE: { toggle: true, units: ['HOUR'], min: 60, max: Number.MAX_SAFE_INTEGER },
  DSP_APPROVE_ESCALATE: { toggle: true, units: ['HOUR'], min: 60, max: Number.MAX_SAFE_INTEGER },
  DSC_ESCALATE_RM: { toggle: true, units: ['DAY'], min: 1440, max: 30 * 1440 },
  DSC_ESCALATE_FG: { toggle: true, units: ['DAY'], min: 0, max: 30 * 1440, allowZero: true },
  DSC_APPROVAL_RM: { toggle: false, units: ['DAY'], min: 1440, max: 60 * 1440 },
  DSC_APPROVAL_FG: { toggle: false, units: ['DAY'], min: 1440, max: 60 * 1440 }
}
export const slaMetaOf = (code: string, unit: SlaUnit): SlaMeta =>
  SLA_META[code] ?? {
    toggle: false,
    units: [unit],
    min: unit === 'PERCENT' ? 1 : unit === 'DAY' ? 1440 : unit === 'HOUR' ? 60 : 1,
    max: unit === 'PERCENT' ? 100 : Number.MAX_SAFE_INTEGER
  }

/* ------------------------------------------------------------ gudang satelit WH-SGI */

export const SGI_WAREHOUSE_ID = seedId(SEED_GROUP.warehouse, 3)
export const SGI_ZONE_ID = seedId(SEED_GROUP.warehouseZone, 300)
const SGI_RACKS = ['S01', 'S02']
const pad2 = (value: number) => String(value).padStart(2, '0')
export const binCode = (rack: string, bay: number, level: number, position: number) =>
  `${rack}-B${pad2(bay)}-L${pad2(level)}-P${pad2(position)}`

const scopeDefaults = (code: string) =>
  SLA_META[code]?.scope ? Object.fromEntries(SLA_META[code].scope.map(def => [def.key, def.default])) : null

export function applyMasterData(seed: SeedTables): SeedTables {
  /* mt_product — atribut tampilan SKU (kolom tambahan ERD). */
  const meta = new Map(SKU_META.map(entry => [entry[0], entry]))
  const mt_product: LocalRow[] = (seed.mt_product ?? []).map(row => {
    const entry = meta.get(String(row.code))
    return {
      ...row,
      sku_code: entry?.[1] ?? null,
      cigarette_kind: entry?.[2] ?? null,
      gtin_pack: entry?.[3] ?? null,
      dimensions: entry?.[4] ?? {},
      inactive_reason: null
    }
  })

  const mt_unit: LocalRow[] = (seed.mt_unit ?? []).map(row => ({
    ...row,
    description: UNIT_NOTE[String(row.code)] ?? null,
    inactive_reason: null
  }))

  /* Rak RM: lokasi seed tetap satu posisi per level; posisi tambahan dibuat lewat Master Lokasi Gudang. */
  const racks = seed.mt_rack ?? []
  const baseBins = (seed.mt_rack_bin ?? []).map(bin => ({
    ...bin,
    position: String(bin.code).split('-')[3] ?? 'P01',
    inactive_reason: null
  }))

  /* Gudang satelit Sansugi: zona simpan + dua rak (3 bay × 2 level × 3 posisi). */
  const sgiBins: LocalRow[] = []
  let sgiNo = 0
  SGI_RACKS.forEach(rack => {
    for (let bay = 1; bay <= 3; bay += 1)
      for (let level = 1; level <= 2; level += 1)
        for (let position = 1; position <= 3; position += 1) {
          sgiNo += 1
          sgiBins.push({
            ...seedAudit,
            id: seedId(SEED_GROUP.rackBin, 500 + sgiNo),
            rack_id: seedId(SEED_GROUP.rack, 4 + SGI_RACKS.indexOf(rack)),
            code: binCode(rack, bay, level, position),
            level: `L${pad2(level)}`,
            column: `B${pad2(bay)}`,
            position: `P${pad2(position)}`,
            inactive_reason: null,
            is_active: true
          })
        }
  })

  const sla_wms_setting: LocalRow[] = SLA_DEFS.map(([code, group, name, description, value, unit], index) => ({
    ...seedAudit,
    id: seedId(SEED_GROUP.sla, index + 1),
    code,
    name,
    group_code: group,
    setting_kind: 'THRESHOLD',
    unit,
    default_value: value,
    value,
    workflow_id: null,
    description,
    sort: index + 1,
    changed_reason: null,
    is_enabled: true,
    scope_json: scopeDefaults(code),
    is_active: true
  }))

  return {
    ...seed,
    mt_product,
    mt_unit,
    mt_warehouses: [
      ...(seed.mt_warehouses ?? []),
      {
        ...seedAudit,
        id: SGI_WAREHOUSE_ID,
        code: 'WH-SGI',
        name: 'Gudang Sansugi (Satelit RM)',
        kind: 'SGI',
        is_active: true
      }
    ],
    warehouse_zone: [
      ...(seed.warehouse_zone ?? []),
      {
        ...seedAudit,
        id: SGI_ZONE_ID,
        code: 'SGI-STORAGE',
        name: 'Area Rak Sansugi',
        warehouse_id: SGI_WAREHOUSE_ID,
        zone_type: 'STORAGE',
        is_active: true
      }
    ],
    mt_rack: [
      ...racks,
      ...SGI_RACKS.map((code, index) => ({
        ...seedAudit,
        id: seedId(SEED_GROUP.rack, 4 + index),
        code,
        name: null,
        zone_id: SGI_ZONE_ID,
        category_restriction_id: null,
        is_active: true
      }))
    ],
    mt_rack_bin: [...baseBins, ...sgiBins],
    sla_wms_setting
  }
}
